import React, {useEffect, useState} from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import {Close as CloseIcon, ExpandMore as ExpandMoreIcon} from '@mui/icons-material';
import {AIService} from '../../types';
import {codeBlockStyles} from '../../styles/codeBlockStyles';

interface AIServiceDialogProps {
    open: boolean;
    onClose: () => void;
    aiService?: AIService | null;
    onSave: (aiService: Partial<AIService>) => void;
}

const PROVIDERS = [
    'openai',
    'perplexity',
    'anthropic',
    'google',
    'pika',
    'klingai',
    'luma',
    'ideogram',
    'custom'
];

const TASK_OPTIONS = [
    'research',
    'advanced_research',
    'multi_prompt_photo',
    'description',
    'image_generation',
    'prompt_generation',
    'funnel_prompt_generation',
    'video_generation',
    'funnel_generation'
];

const ACCESS_LEVELS = [
    'system',
    'basic',
    'advanced',
    'enterprise'
];

const AIServiceDialog: React.FC<AIServiceDialogProps> = ({
                                                             open,
                                                             onClose,
                                                             aiService,
                                                             onSave
                                                         }) => {
    const [formData, setFormData] = useState<Partial<AIService>>({
        name: '',
        code: '',
        provider: 'openai',
        description: '',
        supported_tasks: [],
        is_active: true,
        access_level: 'system',
        sort_order: 0,
        system_config: {},
        default_settings: {},
        pricing_config: {},
        task_specific_settings: {}
    });

    const [jsonErrors, setJsonErrors] = useState({
        system_config: '',
        default_settings: '',
        pricing_config: '',
        task_specific_settings: ''
    });

    // JSON strings for editing
    const [jsonStrings, setJsonStrings] = useState({
        system_config: '{}',
        default_settings: '{}',
        pricing_config: '{}',
        task_specific_settings: '{}'
    });

    useEffect(() => {
        if (aiService) {
            setFormData({
                ...aiService,
                supported_tasks: aiService.supported_tasks || [],
                system_config: aiService.system_config || {},
                default_settings: aiService.default_settings || {},
                pricing_config: aiService.pricing_config || {},
                task_specific_settings: aiService.task_specific_settings || {}
            });

            // Initialize JSON strings
            setJsonStrings({
                system_config: JSON.stringify(aiService.system_config || {}, null, 2),
                default_settings: JSON.stringify(aiService.default_settings || {}, null, 2),
                pricing_config: JSON.stringify(aiService.pricing_config || {}, null, 2),
                task_specific_settings: JSON.stringify(aiService.task_specific_settings || {}, null, 2)
            });
        } else {
            setFormData({
                name: '',
                code: '',
                provider: 'openai',
                description: '',
                supported_tasks: [],
                is_active: true,
                access_level: 'system',
                sort_order: 0,
                system_config: {},
                default_settings: {},
                pricing_config: {},
                task_specific_settings: {}
            });

            setJsonStrings({
                system_config: '{}',
                default_settings: '{}',
                pricing_config: '{}',
                task_specific_settings: '{}'
            });
        }

        setJsonErrors({
            system_config: '',
            default_settings: '',
            pricing_config: '',
            task_specific_settings: ''
        });
    }, [aiService, open]);

    const handleFieldChange = (field: keyof AIService, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSupportedTasksChange = (event: any) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            supported_tasks: typeof value === 'string' ? value.split(',') : value
        }));
    };

    const handleJsonChange = (field: 'system_config' | 'default_settings' | 'pricing_config' | 'task_specific_settings', value: string) => {
        setJsonStrings(prev => ({
            ...prev,
            [field]: value
        }));

        // Validate JSON
        try {
            const parsed = JSON.parse(value);
            setFormData(prev => ({
                ...prev,
                [field]: parsed
            }));
            setJsonErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        } catch (error) {
            setJsonErrors(prev => ({
                ...prev,
                [field]: 'Invalid JSON format'
            }));
        }
    };

    const handleSave = () => {
        // Check for JSON errors
        const hasErrors = Object.values(jsonErrors).some(error => error !== '');
        if (hasErrors) {
            return;
        }

        onSave(formData);
    };

    const getSystemConfigTemplate = (provider: string) => {
        const templates = {
            openai: {
                base_url: "https://api.openai.com/v1",
                auth_type: "bearer_token",
                system_api_key: "",
                default_model: "gpt-4o",
                available_models: ["gpt-4o", "gpt-4o-mini", "gpt-3.5-turbo"],
                rate_limits: {
                    requests_per_minute: 500,
                    tokens_per_minute: 150000
                },
                endpoints: {
                    chat: "/chat/completions",
                    embeddings: "/embeddings"
                }
            },
            perplexity: {
                base_url: "https://api.perplexity.ai",
                auth_type: "bearer_token",
                system_api_key: "",
                default_model: "sonar",
                context_length: 128000,
                rate_limits: {
                    requests_per_day: 2000,
                    requests_per_minute: 100
                }
            },
            anthropic: {
                base_url: "https://api.anthropic.com/v1",
                auth_type: "x-api-key",
                system_api_key: "",
                default_model: "claude-3-sonnet-20240229",
                available_models: ["claude-3-sonnet-20240229", "claude-3-haiku-20240307"],
                rate_limits: {
                    requests_per_minute: 100,
                    tokens_per_minute: 40000
                }
            },
            google: {
                base_url: "https://generativelanguage.googleapis.com/v1",
                auth_type: "api_key",
                system_api_key: "",
                default_model: "gemini-pro",
                available_models: ["gemini-pro", "gemini-pro-vision"],
                rate_limits: {
                    requests_per_minute: 60
                }
            }
        };
        return templates[provider as keyof typeof templates] || {};
    };

    const getDefaultSettingsTemplate = (provider: string) => {
        const templates = {
            openai: {
                temperature: 0.7,
                max_tokens: 2000,
                top_p: 1.0,
                frequency_penalty: 0,
                presence_penalty: 0
            },
            perplexity: {
                temperature: 0.7,
                max_tokens: 2000,
                top_p: 0.9
            },
            anthropic: {
                temperature: 0.7,
                max_tokens: 2000,
                top_p: 1.0
            }
        };
        return templates[provider as keyof typeof templates] || {temperature: 0.7, max_tokens: 2000};
    };

    const getPricingConfigTemplate = (provider: string) => {
        const templates = {
            openai: {
                model: "pay_per_token",
                currency: "USD",
                pricing: {
                    "gpt-4o": {
                        input_per_million: 5.0,
                        output_per_million: 15.0
                    },
                    "gpt-4o-mini": {
                        input_per_million: 0.15,
                        output_per_million: 0.6
                    }
                }
            },
            perplexity: {
                model: "pay_per_request",
                currency: "USD",
                cost_per_request: 0.002
            },
            anthropic: {
                model: "pay_per_token",
                currency: "USD",
                pricing: {
                    "claude-3-sonnet-20240229": {
                        input_per_million: 3.0,
                        output_per_million: 15.0
                    }
                }
            }
        };
        return templates[provider as keyof typeof templates] || {model: "pay_per_request", currency: "USD"};
    };

    const getTaskSpecificSettingsTemplate = (provider: string) => {
        const templates = {
            perplexity: {
                "advanced_research": {
                    temperature: 0.7,
                    max_tokens: 4000,
                    top_p: 0.9,
                    search_recency_filter: "year",
                    web_search_options: {
                        search_context_size: "high"
                    }
                },
                "research": {
                    temperature: 0.7,
                    max_tokens: 3500,
                    top_p: 0.9,
                    search_recency_filter: "year"
                },
                "multi_prompt_photo": {
                    temperature: 0.8,
                    max_tokens: 2000,
                    top_p: 1.0
                }
            },
            openai: {
                "research": {
                    temperature: 0.3,
                    max_tokens: 3000,
                    top_p: 0.9,
                    response_format: {"type": "json_object"}
                },
                "advanced_research": {
                    temperature: 0.3,
                    max_tokens: 4000,
                    top_p: 0.9,
                    response_format: {"type": "json_object"}
                },
                "description": {
                    temperature: 0.5,
                    max_tokens: 1000,
                    top_p: 0.95
                },
                "image_generation": {
                    temperature: 0.8,
                    max_tokens: 2000,
                    top_p: 1.0
                }
            },
            anthropic: {
                "research": {
                    temperature: 0.3,
                    max_tokens: 3000,
                    top_p: 0.9
                },
                "advanced_research": {
                    temperature: 0.3,
                    max_tokens: 4000,
                    top_p: 0.9
                },
                "description": {
                    temperature: 0.5,
                    max_tokens: 1500,
                    top_p: 0.95
                }
            }
        };
        return templates[provider as keyof typeof templates] || {};
    };

    const applyTemplate = (field: 'system_config' | 'default_settings' | 'pricing_config' | 'task_specific_settings') => {
        let template = {};
        switch (field) {
            case 'system_config':
                template = getSystemConfigTemplate(formData.provider || 'openai');
                break;
            case 'default_settings':
                template = getDefaultSettingsTemplate(formData.provider || 'openai');
                break;
            case 'pricing_config':
                template = getPricingConfigTemplate(formData.provider || 'openai');
                break;
            case 'task_specific_settings':
                template = getTaskSpecificSettingsTemplate(formData.provider || 'openai');
                break;
        }

        const jsonString = JSON.stringify(template, null, 2);
        setJsonStrings(prev => ({
            ...prev,
            [field]: jsonString
        }));
        setFormData(prev => ({
            ...prev,
            [field]: template
        }));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Typography variant="h6">
                    {aiService ? 'Edit AI Service' : 'Create AI Service'}
                </Typography>
                <Button onClick={onClose} size="small">
                    <CloseIcon/>
                </Button>
            </DialogTitle>

            <DialogContent sx={{p: 3}}>
                <Box sx={{display: 'grid', gap: 3}}>
                    {/* Basic Information */}
                    <Paper variant="outlined" sx={{p: 3}}>
                        <Typography variant="h6" gutterBottom color="primary">
                            Basic Information
                        </Typography>
                        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(2, 1fr)'}, gap: 2}}>
                            <TextField
                                fullWidth
                                label="Service Name"
                                value={formData.name}
                                onChange={(e) => handleFieldChange('name', e.target.value)}
                                required
                            />
                            <TextField
                                fullWidth
                                label="Service Code"
                                value={formData.code}
                                onChange={(e) => handleFieldChange('code', e.target.value)}
                                required
                                helperText="Unique identifier (e.g., openai_gpt4, perplexity_sonar)"
                            />
                            <FormControl fullWidth>
                                <InputLabel>Provider</InputLabel>
                                <Select
                                    value={formData.provider}
                                    onChange={(e) => handleFieldChange('provider', e.target.value)}
                                    label="Provider"
                                >
                                    {PROVIDERS.map(provider => (
                                        <MenuItem key={provider} value={provider}>
                                            {provider.charAt(0).toUpperCase() + provider.slice(1)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel>Access Level</InputLabel>
                                <Select
                                    value={formData.access_level}
                                    onChange={(e) => handleFieldChange('access_level', e.target.value)}
                                    label="Access Level"
                                >
                                    {ACCESS_LEVELS.map(level => (
                                        <MenuItem key={level} value={level}>
                                            {level.charAt(0).toUpperCase() + level.slice(1)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Box sx={{gridColumn: {xs: '1', md: '1 / -1'}}}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    value={formData.description}
                                    onChange={(e) => handleFieldChange('description', e.target.value)}
                                    multiline
                                    rows={2}
                                />
                            </Box>
                            <FormControl fullWidth>
                                <InputLabel>Supported Tasks</InputLabel>
                                <Select
                                    multiple
                                    value={formData.supported_tasks || []}
                                    onChange={handleSupportedTasksChange}
                                    input={<OutlinedInput label="Supported Tasks"/>}
                                    renderValue={(selected) => (
                                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                            {selected.map((value) => (
                                                <Chip key={value} label={value} size="small"/>
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {TASK_OPTIONS.map(task => (
                                        <MenuItem key={task} value={task}>
                                            {task.replace('_', ' ').charAt(0).toUpperCase() + task.replace('_', ' ').slice(1)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
                                <TextField
                                    label="Sort Order"
                                    type="number"
                                    value={formData.sort_order}
                                    onChange={(e) => handleFieldChange('sort_order', parseInt(e.target.value) || 0)}
                                    sx={{flex: 1}}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.is_active || false}
                                            onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                                        />
                                    }
                                    label="Active"
                                />
                            </Box>
                        </Box>
                    </Paper>

                    {/* System Configuration */}
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                            <Typography variant="h6">⚙️ System Configuration</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{display: 'grid', gap: 2}}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <Typography variant="body2" color="text.secondary">
                                        API endpoints, rate limits, authentication settings, system API key.
                                        Только технические параметры без бизнес-логики.
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => applyTemplate('system_config')}
                                    >
                                        Apply {formData.provider} Template
                                    </Button>
                                </Box>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={8}
                                    label="System Configuration (JSON)"
                                    value={jsonStrings.system_config}
                                    onChange={(e) => handleJsonChange('system_config', e.target.value)}
                                    error={!!jsonErrors.system_config}
                                    helperText={jsonErrors.system_config || 'JSON configuration for API endpoints, models, and system settings'}
                                    sx={{fontFamily: 'monospace'}}
                                />
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* Default Settings */}
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                            <Typography variant="h6">🎛️ Default Settings</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{display: 'grid', gap: 2}}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <Typography variant="body2" color="text.secondary">
                                        Базовые AI параметры по умолчанию: temperature, max_tokens, top_p, etc.
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => applyTemplate('default_settings')}
                                    >
                                        Apply {formData.provider} Template
                                    </Button>
                                </Box>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={6}
                                    label="Default Settings (JSON)"
                                    value={jsonStrings.default_settings}
                                    onChange={(e) => handleJsonChange('default_settings', e.target.value)}
                                    error={!!jsonErrors.default_settings}
                                    helperText={jsonErrors.default_settings || 'Default parameters for AI model requests'}
                                    sx={{fontFamily: 'monospace'}}
                                />
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* Task-Specific Settings */}
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                            <Typography variant="h6">🎯 Task-Specific Settings</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{display: 'grid', gap: 2}}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <Typography variant="body2" color="text.secondary">
                                        Оптимизированные настройки для разных типов задач.
                                        Для Perplexity используйте temperature: 0.7 и search_recency_filter: "year"
                                        для лучшего качества исследований.
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => applyTemplate('task_specific_settings')}
                                    >
                                        Apply {formData.provider} Template
                                    </Button>
                                </Box>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={8}
                                    label="Task-Specific Settings (JSON)"
                                    value={jsonStrings.task_specific_settings}
                                    onChange={(e) => handleJsonChange('task_specific_settings', e.target.value)}
                                    error={!!jsonErrors.task_specific_settings}
                                    helperText={jsonErrors.task_specific_settings || 'Task-specific AI model parameters and settings'}
                                    sx={{fontFamily: 'monospace'}}
                                />
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* Pricing Configuration */}
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                            <Typography variant="h6">💰 Pricing Configuration</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{display: 'grid', gap: 2}}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <Typography variant="body2" color="text.secondary">
                                        Pricing model, rates per token/request, currency settings
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => applyTemplate('pricing_config')}
                                    >
                                        Apply {formData.provider} Template
                                    </Button>
                                </Box>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={6}
                                    label="Pricing Configuration (JSON)"
                                    value={jsonStrings.pricing_config}
                                    onChange={(e) => handleJsonChange('pricing_config', e.target.value)}
                                    error={!!jsonErrors.pricing_config}
                                    helperText={jsonErrors.pricing_config || 'Pricing model and cost calculation settings'}
                                    sx={{fontFamily: 'monospace'}}
                                />
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* Preview Current Settings */}
                    {aiService && (
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                <Typography variant="h6">📋 Current Settings Preview</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{display: 'grid', gap: 2}}>
                                    <Typography variant="body2" color="text.secondary">
                                        Предварительный просмотр конфигурации в формате "промпт-центричной архитектуры"
                                    </Typography>
                                    <Paper variant="outlined" sx={{p: 2}}>
                                        <Typography variant="caption" display="block" gutterBottom
                                                    sx={{fontWeight: 'bold'}}>
                                            Effective Configuration:
                                        </Typography>
                                        <Box
                                            component="pre"
                                            sx={{
                                                ...codeBlockStyles,
                                                maxHeight: '200px'
                                            }}
                                        >
                                            {JSON.stringify({
                                                system_config: formData.system_config,
                                                default_settings: formData.default_settings,
                                                task_specific_settings: formData.task_specific_settings,
                                                pricing_config: formData.pricing_config
                                            }, null, 2)}
                                        </Box>
                                    </Paper>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{p: 3, gap: 1}}>
                <Button onClick={onClose} variant="outlined">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={Object.values(jsonErrors).some(error => error !== '')}
                >
                    {aiService ? 'Update Service' : 'Create Service'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AIServiceDialog;
